import React, { type ReactNode, useCallback, useContext, useRef } from 'react';

import { apiClient } from 'src/modules/api';

import { usePreflight } from '../PreflightProvider';

import type {
  InitialProfileAnalytics,
  ProfileAnalytics,
  ProfileAnalyticsContext,
  RolesAnalytics,
  SkillsAnalytics,
} from './types';

const ctx = React.createContext<ProfileAnalyticsContext | undefined>(undefined);

const calculateRoleDelta = (
  roles: ProfileAnalytics['roles'],
): ProfileAnalytics['rolesAnalytics'] =>
  roles.reduce(
    (acc, value) => {
      switch (value.type) {
        case 'new':
          return { ...acc, add: acc.add + 1 };
        case 'edited':
          return { ...acc, edit: acc.edit + 1 };
        case 'deleted':
          return { ...acc, delete: acc.delete + 1 };
        case 'existing':
          return acc;
      }
    },
    {
      add: 0,
      edit: 0,
      delete: 0,
    },
  );

export const ProfileAnalyticsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const {
    request: { advertiser_id, client_id },
    preflight: { sessionId },
  } = usePreflight();

  /**
   * Implement `useRef` instead of `useState` to prevent large scale re-renders
   * when updating state.
   */
  const profileAnalytics = useRef<ProfileAnalytics>({
    roles: [],
    skills: [],
    skillsAnalytics: { add: 0, delete: 0 },
    rolesAnalytics: { add: 0, edit: 0, delete: 0 },
    totalResumes: 0,
    version: 1,
    exited: undefined,
  });

  const storeProfileAnalytics = useCallback(async () => {
    apiClient
      .storeProfileAnalytics({
        advertiser_id,
        client_id,
        sessionId,
        resume: {
          total: profileAnalytics.current.totalResumes,
        },
        skills: profileAnalytics.current.skillsAnalytics,
        roles: profileAnalytics.current.rolesAnalytics,
        version: profileAnalytics.current.version,
        exited: profileAnalytics.current.exited,
      })
      .then(() => {
        profileAnalytics.current.version += 1;
      })
      .catch(() => {
        // Ignore any failures, storing metrics should
        // have no impact on the candidate experience.
      });
  }, [advertiser_id, client_id, sessionId]);

  const captureInitialProfile = useCallback(
    async ({ totalResumes, roles, skills }: InitialProfileAnalytics) => {
      if (profileAnalytics.current.version !== 1) {
        return;
      }

      profileAnalytics.current.skills = skills;
      profileAnalytics.current.roles = roles;
      profileAnalytics.current.totalResumes = totalResumes;

      await storeProfileAnalytics();
    },
    [storeProfileAnalytics],
  );

  const captureSkillsAnalytics = useCallback(
    async ({ skills: updatedSkills }: SkillsAnalytics) => {
      const initialSkills = profileAnalytics.current.skills;

      // Capture which new skills don't exist in the initial profile
      const skillsAdded = updatedSkills.filter(
        (skill) => !initialSkills.includes(skill),
      ).length;

      // Capture which initial skills don't exist in the updated skills
      const skillsDeleted = initialSkills.filter(
        (skill) => !updatedSkills.includes(skill),
      ).length;

      profileAnalytics.current.skillsAnalytics = {
        add: skillsAdded,
        delete: skillsDeleted,
      };

      return storeProfileAnalytics();
    },
    [storeProfileAnalytics],
  );

  const captureRolesAnalytics = useCallback(
    async (event: RolesAnalytics) => {
      const roles = profileAnalytics.current.roles;

      switch (event.action) {
        case 'add':
          // Capture which new roles don't exist as part of the existing roles
          const newRoles = event.roleIds.filter(
            (roleId) => !roles.map(({ id }) => id).includes(roleId),
          );

          profileAnalytics.current.roles = [
            ...roles,
            ...newRoles.map((id) => ({ type: 'new' as const, id })),
          ];

          break;

        case 'edit':
          // Capture which role has been edited
          profileAnalytics.current.roles = roles.map((role) =>
            role.id === event.id && role.type === 'existing'
              ? { ...role, type: 'edited' }
              : role,
          );

          break;

        case 'delete':
          // Capture which existing roles are no longer provided in the updated list of roles
          profileAnalytics.current.roles = roles.flatMap((role) => {
            const isBeingDeleted = !event.roleIds.includes(role.id);

            if (isBeingDeleted && role.type === 'new') {
              return [];
            }

            return isBeingDeleted ? [{ ...role, type: 'deleted' }] : [role];
          });

          break;
      }

      profileAnalytics.current.rolesAnalytics = calculateRoleDelta(
        profileAnalytics.current.roles,
      );

      return storeProfileAnalytics();
    },
    [storeProfileAnalytics],
  );

  const captureExited: ProfileAnalyticsContext['captureExited'] = useCallback(
    async (method) => {
      profileAnalytics.current.exited = method;
      return storeProfileAnalytics();
    },
    [storeProfileAnalytics],
  );

  return (
    <ctx.Provider
      value={{
        captureInitialProfile,
        captureRolesAnalytics,
        captureSkillsAnalytics,
        captureExited,
      }}
    >
      {children}
    </ctx.Provider>
  );
};

export const useProfileAnalytics = () => {
  const profileAnalytics = useContext(ctx);

  if (!profileAnalytics) {
    throw new Error(
      'useProfileAnalytics must be wrapped in ProfileAnalyticsProvider.',
    );
  }

  return profileAnalytics;
};
